import { PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { errorTelemetryProperties, trackException, trackFunnelEvent } from '@/telemetry';

const authority = import.meta.env.VITE_B2C_AUTHORITY as string;
const clientId = import.meta.env.VITE_B2C_CLIENT_ID as string;
const knownAuthority = import.meta.env.VITE_B2C_KNOWN_AUTHORITY as string;
const configuredRedirectUri = import.meta.env.VITE_B2C_REDIRECT_URI as string | undefined;

export const graphScopes = ['https://graph.microsoft.com/User.Read'];

// Prefer the current origin at runtime so deployed environments
// do not redirect back to localhost, falling back to configured value.
const runtimeRedirectUri =
  (typeof window !== 'undefined' && window.location.origin) || configuredRedirectUri || '';

if (!authority || !clientId || !knownAuthority || !runtimeRedirectUri) {
  // eslint-disable-next-line no-console
  console.warn('MSAL config incomplete. Ensure VITE_B2C_* are set and redirect URI is registered.');
}

export const pca = new PublicClientApplication({
  auth: {
    authority,
    clientId,
    knownAuthorities: knownAuthority ? [knownAuthority] : undefined,
    redirectUri: runtimeRedirectUri,
  },
  cache: { cacheLocation: 'sessionStorage' },
});

let initPromise: Promise<void> | null = null;
export async function initMsal() {
  if (!initPromise) {
    initPromise = pca.initialize().catch((e) => {
      initPromise = null;
      throw e;
    });
  }
  return initPromise;
}

export async function handleRedirect(): Promise<AuthenticationResult | null> {
  await initMsal();
  const result = await pca.handleRedirectPromise();
  if (result) pca.setActiveAccount(result.account);
  return result;
}

export async function signIn() {
  await initMsal();
  // If an account from a different authority is cached, clear active account to avoid mismatch
  const acct = pca.getActiveAccount() ?? (pca.getAllAccounts()[0] || null);
  try {
    if (acct) {
      const configuredHost = new URL(authority).host;
      // environment is the authority host saved for the account
      if ((acct as any).environment && (acct as any).environment !== configuredHost) {
        pca.setActiveAccount(null);
      }
    }
  } catch {
    // best effort; ignore URL parsing issues
  }

  try {
    trackFunnelEvent('auth_redirect_started');
    await pca.loginRedirect({ authority, scopes: ['openid', 'profile', ...graphScopes] });
  } catch (err) {
    trackFunnelEvent('auth_redirect_failed', errorTelemetryProperties(err));
    trackException(err, { eventName: 'auth_redirect_failed' });
    throw err;
  }
}

export function getActiveAccount() {
  return pca.getActiveAccount() ?? (pca.getAllAccounts()[0] || null);
}

export async function acquireGraphAccessToken(): Promise<string | null> {
  await initMsal();
  const account = getActiveAccount();
  if (!account) return null;
  try {
    const result = await pca.acquireTokenSilent({ account, authority, scopes: graphScopes });
    return result?.accessToken || null;
  } catch {
    return null;
  }
}

export async function signOut(options?: { redirectTo?: string }) {
  const postLogoutRedirectUri = options?.redirectTo || runtimeRedirectUri || '/';

  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      keepalive: true,
    });
  } catch {
    // best effort; continue with MSAL logout
  }

  await initMsal();
  const account = getActiveAccount() || undefined;

  try {
    await pca.logoutRedirect({
      authority,
      account,
      postLogoutRedirectUri,
    });
    return;
  } catch {
    try {
      pca.setActiveAccount(null);
    } catch {
      // ignore cache clearing errors
    }
  }

  if (typeof window !== 'undefined') {
    window.location.href = postLogoutRedirectUri;
  }
}
